import moment from 'moment';
import Immutable from 'immutable';
import { createSelector } from 'reselect';
import {
  SUBSCRIPTION_PLAN_TYPE_EDUCATOR,
  SUBSCRIPTION_PLAN_STATUS_TRIAL,
  SUBSCRIPTION_PLAN_STATUS_ACTIVE,
  SUBSCRIPTION_PLAN_STATUS_EXPIRED,
  SUBSCRIPTION_PLAN_BILLING_METHOD_INVOICE,
  SUBSCRIPTION_PLAN_TYPE_TEACH,
  SUBSCRIPTION_PLAN_STATUS_PAST_DUE,
  SUBSCRIPTION_PLAN_STATUS_CANCEL_PENDING,
  SUBSCRIPTION_PLAN_STATUS_CANCELLED,
  SUBSCRIPTION_PLAN_ACTION_CANCEL_PERSONAL,
} from './constants';
import { currentUserIdSelector } from '../Auth/selectors';
import {
  selectSubscriptionPlans,
  selectDefaultEducatorSubscriptionPlan,
} from '../SubscriptionPlans/selectors';
import {
  priceFormat,
  capitalizeFirstLetter,
} from '../../web/utilities/utilities';
import { validUserSubscriptionPlanSettings } from './selectors.test.data';
import { id } from '../../web/utilities/functions';
import { filterUserSubscriptionPlansManage } from './helpers';
import { selectQueryObject } from '../location/selectors.web';
import { TabOptionTypes } from '../../web/containers/SubscriptionsAdmin/types';

export const selectUserSubscriptionPlans = (state) =>
  state.UserSubscriptionPlans || Immutable.Map();

export const selectCurrentSelectedUserSubscription = createSelector(
  selectUserSubscriptionPlans,
  currentUserIdSelector,
  (subscriptionPlans, currentUserId) =>
    subscriptionPlans
      .getIn(
        ['byUser', currentUserId, 'currentSelectedUserSubscription'],
        Immutable.Map()
      )
      .toJS()
);

export const selectUserSubscriptionIdFromUrl = createSelector(
  selectQueryObject,
  ({ subscriptionId }) => subscriptionId ?? ''
);

export const selectManageSubscriptionsView = createSelector(
  selectQueryObject,
  ({ view }) => view ?? TabOptionTypes.ACCOUNT_USAGE
);

export const selectCurrentUserSubscriptionPlans = createSelector(
  selectUserSubscriptionPlans,
  currentUserIdSelector,
  (subscriptionPlans, currentUserId) =>
    subscriptionPlans.getIn(['byUser', currentUserId, 'plans'], Immutable.Map())
);

export const selectCurrentUserSubscriptionPlanSummaries = createSelector(
  selectCurrentUserSubscriptionPlans,
  (subscriptionPlans) =>
    subscriptionPlans.toList().toJS().filter(filterUserSubscriptionPlansManage)
);

export const selectUserSubscriptionPlanSummariesById = createSelector(
  selectUserSubscriptionPlans,
  (subscriptionPlans) => subscriptionPlans.get('byId', Immutable.Map()).toJS()
);

export const makeSelectUserSubscriptionPlanById = (userSubscriptionPlanId) =>
  createSelector(selectUserSubscriptionPlans, (subscriptionPlans) =>
    subscriptionPlans.getIn(['byId', userSubscriptionPlanId], null)
  );

export const selectCurrentUserEducatorSubscription = createSelector(
  selectCurrentUserSubscriptionPlans,
  (subscriptionPlans) => {
    // there should be one or none
    return subscriptionPlans.find(
      (plan) => plan.get('type') === SUBSCRIPTION_PLAN_TYPE_EDUCATOR,
      null
    );
  }
);

export const selectShowPaymentFailureBanner = createSelector(
  selectCurrentUserSubscriptionPlans,
  (subscriptionPlans) =>
    subscriptionPlans &&
    !!subscriptionPlans.find(
      (plan) => plan.get('status') === SUBSCRIPTION_PLAN_STATUS_PAST_DUE
    )
);

export const selectCurrentUserIsOwnerOfSubscription = createSelector(
  selectCurrentUserSubscriptionPlans,
  currentUserIdSelector,
  (subscriptionPlans, currentUserId) =>
    subscriptionPlans.find((plan) => plan.get('userId') === currentUserId)
);

export const selectCurrentUserSubscriptionPlansHasLoaded = createSelector(
  selectUserSubscriptionPlans,
  currentUserIdSelector,
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans.getIn(
      ['byUser', currentUserId, 'hasLoaded'],
      false
    );
  }
);

export const selectCurrentUserSubscriptionPlanDataHasLoaded = createSelector(
  selectUserSubscriptionPlans,
  currentUserIdSelector,
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans.getIn(
      ['byUser', currentUserId, 'subscriptionDataLoaded'],
      false
    );
  }
);

const getSubscriptionPlan = (
  userSubscriptionPlan,
  subscriptionPlans,
  defaultEducatorSubscriptionPlan
) => {
  const subscriptionPlan = subscriptionPlans.get(
    userSubscriptionPlan.get('planId')
  );
  if (!subscriptionPlan) {
    // this could be an active/expired educator trial with no planId
    if (
      userSubscriptionPlan.get('type') === SUBSCRIPTION_PLAN_TYPE_EDUCATOR &&
      [
        SUBSCRIPTION_PLAN_STATUS_TRIAL,
        SUBSCRIPTION_PLAN_STATUS_EXPIRED,
      ].includes(userSubscriptionPlan.get('status'))
    ) {
      return defaultEducatorSubscriptionPlan;
    }

    // this could be an invoiced educator subscription with a weird plan id, e.g. EDUCATOR_6MONTHS_MYR
    if (
      userSubscriptionPlan.get('type') === SUBSCRIPTION_PLAN_TYPE_EDUCATOR &&
      userSubscriptionPlan.get('billingMethod') ===
        SUBSCRIPTION_PLAN_BILLING_METHOD_INVOICE
    ) {
      return defaultEducatorSubscriptionPlan; // this might not be accurate but it's good enough since we don't actually record what price these subscriptions were charged...
    }
  }
  return subscriptionPlan;
};

const getStatusLabel = (status, expiryDate) => {
  let statusLabel = capitalizeFirstLetter(status);
  switch (status) {
    case SUBSCRIPTION_PLAN_STATUS_TRIAL:
    case SUBSCRIPTION_PLAN_STATUS_ACTIVE: {
      if (expiryDate.isValid()) {
        return [statusLabel, 'Ends on', expiryDate.format('DD MMM YYYY')];
      }
      break;
    }
    case SUBSCRIPTION_PLAN_STATUS_PAST_DUE: {
      statusLabel = 'Payment declined';
      break;
    }
    case SUBSCRIPTION_PLAN_STATUS_CANCEL_PENDING: {
      if (expiryDate.isValid()) {
        return [
          'Cancelled',
          'Available until',
          expiryDate.format('DD MMM YYYY'),
        ];
      }
      break;
    }
    case SUBSCRIPTION_PLAN_STATUS_CANCELLED:
    case SUBSCRIPTION_PLAN_STATUS_EXPIRED: {
      if (expiryDate.isValid()) {
        return [statusLabel, 'Ended on', expiryDate.format('DD MMM YYYY')];
      }
      break;
    }
    default:
      break;
  }
  return [statusLabel];
};

const getAmountLabel = (subscriptionPlan) => {
  const price = subscriptionPlan.get('price', 0);
  const currency = subscriptionPlan.get('currency', null);
  const amount = price && currency ? priceFormat(price, currency, true) : null;
  const billingCycle = subscriptionPlan.get('billingCycle', null);
  if (amount && billingCycle) {
    return [amount, `(${billingCycle})`];
  }
  return ['N/A'];
};

export const selectPaymentMethodSummariesById = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'paymentSummary'], Immutable.Map())
      .toJS();
  }
);

export const selectCumulativeUsagesById = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'cumulativeUsage'], Immutable.Map())
      .toJS();
  }
);

export const selectDailyUsagesById = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'dailyUsages'], Immutable.Map())
      .toJS();
  }
);

export const selectEducatorSeatsById = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'educatorSeats'], Immutable.Map())
      .toJS();
  }
);

export const selectManageSubscriptionsRowData = createSelector(
  [
    selectCurrentUserSubscriptionPlans,
    selectSubscriptionPlans,
    selectDefaultEducatorSubscriptionPlan,
  ],
  (userSubscriptionPlans, subscriptionPlans, defaultEducatorSubscriptionPlan) =>
    userSubscriptionPlans
      .map((userSubscriptionPlan) => {
        const subscriptionPlan = getSubscriptionPlan(
          userSubscriptionPlan,
          subscriptionPlans,
          defaultEducatorSubscriptionPlan
        );
        if (!subscriptionPlan) {
          return null;
        }

        const type = userSubscriptionPlan.get('type');
        const status = userSubscriptionPlan.get('status');
        let billingMethod =
          userSubscriptionPlan.get('billingMethod', null) || 'null';

        let setup = null;
        if (subscriptionPlan.get('type') === SUBSCRIPTION_PLAN_TYPE_TEACH) {
          setup = validUserSubscriptionPlanSettings.getIn(
            [type, subscriptionPlan.get('path'), status, billingMethod],
            null
          );
        } else {
          setup = validUserSubscriptionPlanSettings.getIn(
            [type, status, billingMethod],
            null
          );
        }
        if (!setup) {
          return null; // is not a valid user subscription plan
        }

        const expiryDate = setup.get('showExpiryDate', false)
          ? userSubscriptionPlan.get('expiryDate')
          : null;

        let nextPaymentLabel = 'N/A';
        if (setup.get('showNextPaymentDate', false)) {
          const nextPaymentDate = moment(
            userSubscriptionPlan.get('nextPaymentDate')
          );
          if (nextPaymentDate.isValid()) {
            nextPaymentLabel = nextPaymentDate.format('DD MMM YYYY');
          }
        }

        const amountLabel = setup.get('showAmount', false)
          ? getAmountLabel(subscriptionPlan)
          : ['N/A'];

        billingMethod = setup.get('showPaymentMethod', false)
          ? billingMethod
          : null;

        let endDate = '';
        if (
          setup
            .get('actions')
            .find(
              (action) =>
                action.get('action') ===
                SUBSCRIPTION_PLAN_ACTION_CANCEL_PERSONAL
            )
        ) {
          endDate = moment(userSubscriptionPlan.get('expiryDate')).format(
            'DD MMM YYYY'
          );
        }

        return Immutable.fromJS({
          userSubscriptionPlanId: userSubscriptionPlan.get('id'),
          planName: subscriptionPlan.get('name'),
          planType: type,
          status,
          statusLabel: getStatusLabel(status, moment(expiryDate)),
          nextPaymentLabel,
          amountLabel,
          billingMethod,
          endDate,
        }).merge({
          actions: setup.get('actions', Immutable.List()),
        });
      })
      .filter(id)
      .sort((row) => row.get('planName'))
      .toList()
);

export const selectExistingOwnersAndAdmins = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'ownerAndAdmins'], Immutable.Map())
      .toJS();
  }
);

export const selectLinkedCoursePaths = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'linkedCoursePaths'], Immutable.Map())
      .toJS();
  }
);

export const selectBillingPeriodsById = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'billingPeriods'], Immutable.List())
      .toJS();
  }
);

export const selectUserSubscriptionPlanNamesById = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'planNames'], Immutable.Map())
      .toJS();
  }
);

export const selectLinkedInstitutionPaths = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(
        ['byUser', currentUserId, 'linkedInstitutionPaths'],
        Immutable.Map()
      )
      .toJS();
  }
);

export const selectLinkedInstitutions = createSelector(
  [selectUserSubscriptionPlans, currentUserIdSelector],
  (subscriptionPlans, currentUserId) => {
    return subscriptionPlans
      .getIn(['byUser', currentUserId, 'linkedInstitutions'], Immutable.Map())
      .toJS();
  }
);

export const selectCurrentUserSubscriptionIds = createSelector(
  [selectCurrentUserSubscriptionPlans],
  (subscriptionPlans) => subscriptionPlans.keySeq().toArray()
);
